import Button from '@pelckmans/elementis/components/button';
import Upload from '@pelckmans/elementis/icons/upload';
import Filter from '@pelckmans/elementis/icons/filter';
import Section from '@pelckmans/elementis/components/section';
import { useTranslation } from 'react-i18next';
import ModalContext from '../../../../../contexts/ModalContext';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import StudentFilter from './StudentFilter';
import FilterStudentsModal from './components/filter-students-modal/FilterStudentsModal';
import ResultsTable from './ResultsTable';
import renderStudent from '../../utils/render-student';
import LoadingState from '../../../../../shared-components/LoadingState';
import { useResultsDataContext } from '../../context/ResultsDataContext';
import { NodeWithoutDescription, fetchTocNodes } from '../../../../../api/table-of-content';
import useThrowAsyncError from '../../../../../hooks/useThrowAsyncError';
import useResults from './hooks/useResults';
import { useFilters } from '../../context/filter-context/FilterContext';
import { useNavigate } from 'react-router-dom';
import Badge from '@pelckmans/elementis/components/badge';
import FilterOffIcon from '@pelckmans/elementis/icons/filter-off';

function useTableOfContent() {
  const { selectedModule } = useFilters();
  const [loading, setIsLoading] = useState(false);
  const [toc, setToc] = useState<NodeWithoutDescription[]>([]);
  const handleError = useThrowAsyncError();

  const previousSelectedModuleId = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!selectedModule || previousSelectedModuleId.current === selectedModule.id) return;

    let current = true;

    setIsLoading(true);

    fetchTocNodes(selectedModule.id)
      .then(res => {
        if (current) {
          setToc(res.data);
        }
      })
      .catch(handleError)
      .finally(() => {
        if (current) {
          setIsLoading(false);
        }
      });

    previousSelectedModuleId.current = selectedModule.id;

    return () => {
      current = false;
    };
  }, [handleError, selectedModule]);

  return { toc, loading };
}

export default function StudentTab() {
  const { showModal } = useContext(ModalContext);
  const {
    loading: resultsDataContextLoading,
    groups,
    scorableItemsPerSchoolYearPerModule,
    members,
  } = useResultsDataContext();
  const { toc, loading: tocLoading } = useTableOfContent();
  const { results, loading: resultsLoading } = useResults();
  const navigate = useNavigate();

  const { selectedMembers, setSelectedMembers, selectedSchoolYear, selectedModule, selectedGroups } = useFilters();

  const loading = Object.values(resultsDataContextLoading).some(Boolean) || resultsLoading || tocLoading;

  const [showDetailView, setShowDetailView] = useState(false);

  const { t } = useTranslation();

  const hasNoMembers = useMemo(() => {
    return groups.reduce((total, group) => total + group.acceptedMemberCount, 0) === 0;
  }, [groups]);

  const resultsTableContainerRef = useRef<HTMLDivElement>(null);

  const membersToShow = useMemo(
    () => (!selectedGroups.length ? [] : selectedMembers[selectedGroups[0].id] || []),
    [selectedGroups, selectedMembers],
  );

  const title = !showDetailView
    ? t('PAGES.RESULTS.PER_STUDENT.SECTION_TITLE')
    : t('PAGES.RESULTS.PER_STUDENT.SECTION_TITLE_SINGLE_STUDENT', {
        studentName: renderStudent(membersToShow[0]),
      });

  const backProps = useMemo(() => {
    return showDetailView
      ? {
          onBackClick: () => {
            navigate(-1);
            setShowDetailView(false);
          },
          backButtonLabel: t('PAGES.RESULTS.PER_STUDENT.BACK_BUTTON_A11Y_LABEL'),
        }
      : {};
  }, [navigate, showDetailView, t]);

  const filtersDisabled =
    (scorableItemsPerSchoolYearPerModule[selectedSchoolYear?.id || '']?.[selectedModule?.id || ''] || []).length === 0;

  const filterProps = useMemo(() => {
    const membersFilterActive =
      membersToShow.length > 0 && membersToShow.length !== members[selectedGroups[0!].id || '']?.length;

    return !hasNoMembers && !showDetailView
      ? {
          filter: <StudentFilter filtersDisabled={filtersDisabled} />,
          filterActions: (
            <>
              <Button
                startIcon={<Filter />}
                size="sm"
                prominence="neutral"
                disabled={filtersDisabled}
                onClick={() => {
                  showModal(
                    <FilterStudentsModal
                      id="filter-students-modal"
                      allMembers={members[selectedGroups[0!].id || ''] || []}
                      selectedMembers={membersToShow}
                      setSelectedMembers={members => {
                        setSelectedMembers(members.map(m => `${selectedGroups[0!].id};${m.id}`));
                        setShowDetailView(members.length === 1);
                      }}
                    />,
                  );
                }}
                badge={
                  membersFilterActive ? (
                    <Badge prominence="ghost" size="sm">
                      {membersToShow.length}
                    </Badge>
                  ) : undefined
                }
                key="open-filter-students-modal"
              >
                {t('PAGES.RESULTS.PER_STUDENT.FILTER.STUDENTS.OPEN_MODAL_BUTTON')}
              </Button>
              {membersFilterActive && (
                <Button
                  startIcon={<FilterOffIcon data-testid="filter-off-icon" />}
                  aria-label={t('PAGES.RESULTS.PER_STUDENT.FILTER.STUDENTS.CLEAR')}
                  onClick={() => setSelectedMembers([])}
                  key="clear-students-filter"
                />
              )}
            </>
          ),
          filterActionsGroupRole: 'group',
          filterActionsAriaLabel: t('PAGES.RESULTS.PER_STUDENT.FILTER.STUDENTS.BUTTON_GROUP_A11Y_LABEL'),
        }
      : {};
  }, [
    filtersDisabled,
    hasNoMembers,
    members,
    membersToShow,
    selectedGroups,
    setSelectedMembers,
    showDetailView,
    showModal,
    t,
  ]);

  const actionProps = useMemo(() => {
    return !hasNoMembers && !showDetailView
      ? {
          actions: (
            <Button prominence="secondary" size="sm" startIcon={<Upload data-testid="export-icon" />}>
              {t('PAGES.RESULTS.EXPORT')}
            </Button>
          ),
          actionGroupRole: undefined,
          actionGroupAriaLabel: undefined,
        }
      : {};
  }, [hasNoMembers, showDetailView, t]);

  return (
    <Section title={title} {...actionProps} {...backProps} {...filterProps}>
      <div className="results-table-container" ref={resultsTableContainerRef}>
        {loading ? (
          <LoadingState variant="table" aria-label={t('PAGES.RESULTS.PER_STUDENT.LOADING_STATE.A11Y_LABEL')} />
        ) : (
          <ResultsTable
            ref={resultsTableContainerRef}
            showDetailView={showDetailView}
            hasNoMembers={hasNoMembers}
            results={results}
            toc={toc}
            onClearFilter={() => setShowDetailView(false)}
          />
        )}
      </div>
    </Section>
  );
}
